import type { ConsentDetailsApiResponse, ConsentStatus, ConsentSubmitResponse } from '@/services'

export type ConsentAction = 'approve' | 'decline'
export type ConsentStatusVariant = 'granted' | 'declined' | 'info' | 'error'
export type ConsentUiStatus = ConsentStatus | 'UNKNOWN'

export const ACTIONABLE_STATUSES: ConsentStatus[] = ['CONSENT_REQUESTED', 'CONSENT_OVERRIDED']

export const KNOWN_STATUSES: ConsentStatus[] = [
	'CONSENT_REQUESTED',
	'CONSENT_GRANTED',
	'CONSENT_REVOKED',
	'CONSENT_REMINDED',
	'CONSENT_OVERRIDED',
	'CONSENT_NOT_REQUESTED',
	'CONSENT_LINK_INVALIDATED',
	'CONSENT_PARAMETER_INVALID',
	'CONSENT_REJECTED'
]

const STATUS_VARIANT_MAP: Partial<Record<ConsentStatus, ConsentStatusVariant>> = {
	CONSENT_GRANTED: 'granted',
	CONSENT_REJECTED: 'declined',
	CONSENT_REVOKED: 'error',
	CONSENT_REMINDED: 'info',
	CONSENT_NOT_REQUESTED: 'error',
	CONSENT_LINK_INVALIDATED: 'error',
	CONSENT_PARAMETER_INVALID: 'error'
}

export interface ConsentData {
	requesterName: string
	companyName: string
	companyNpwp: string
	serviceName: string
	status: ConsentUiStatus
}

export const DEFAULT_CONSENT_DATA: ConsentData = {
	requesterName: '',
	companyName: '',
	companyNpwp: '',
	serviceName: '',
	status: 'CONSENT_REQUESTED'
}

export function normalizeConsentData(
	data: ConsentDetailsApiResponse | null,
	current: ConsentData
): ConsentData {
	if (!data) return current
	const rawStatus = (data.consent_status || '').toUpperCase() as ConsentStatus
	const status = KNOWN_STATUSES.includes(rawStatus) ? rawStatus : 'UNKNOWN'
	return {
		requesterName: data.requester_name || '',
		companyName: data.consenter_name || '',
		companyNpwp: data.consenter_npwp || '-',
		serviceName: data.service || '',
		status
	}
}

export function getStatusVariant(status: ConsentUiStatus): ConsentStatusVariant {
	return STATUS_VARIANT_MAP[status as ConsentStatus] ?? 'error'
}

export type SnackbarType = 'info' | 'success' | 'error'
type TranslateFn = (key: string) => string

function getUnhandledErrorMessage(t: TranslateFn): string {
	const fallback500Key = 'consent.submit.500'
	const translated500 = t(fallback500Key)
	return translated500 !== fallback500Key ? translated500 : t('errors.general-error')
}

export function resolveSubmitMessage(
	response: ConsentSubmitResponse | null,
	action: ConsentAction,
	t: TranslateFn
): { text: string; type: SnackbarType } {
	const defaultKey = action === 'approve' ? 'consent.approve-success' : 'consent.decline-success'
	if (!response) {
		return { text: t(defaultKey), type: 'success' }
	}
	const { code, data, message, status } = response
	if (status === 'error' || (code && code !== 200 && code !== '200')) {
		const i18nKey = `consent.submit.${code}`
		const translated = t(i18nKey)
		if (translated !== i18nKey) return { text: translated, type: 'error' }
		return { text: getUnhandledErrorMessage(t), type: 'error' }
	}
	return { text: data || message || t(defaultKey), type: 'success' }
}

interface ApiError {
	response?: {
		data?: {
			code?: number | string
			data?: string
			message?: string
		}
	}
}

export function getErrorMessage(err: unknown, t: TranslateFn): string {
	const dataInfo = (err as ApiError)?.response?.data
	if (!dataInfo) return t('errors.general-error')
	if (dataInfo.code) {
		const i18nKey = `consent.submit.${dataInfo.code}`
		const translated = t(i18nKey)
		if (translated !== i18nKey) return translated
	}
	return getUnhandledErrorMessage(t)
}
